<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="element-box">
        <CoachingOverviewData
            :coaching="coachingData"
            :calculatedUnits="calculatedUnitsData"/>

        <Menu :coaching="coachingData"
              :currentUser="piniaCurrentUser"/>

        <div class="row text-end mt-3">
          <div class="col-12">
            <button type="button" class="btn btn-primary" @click="handleModalForm">
              Zwischenbericht hinzufügen
            </button>
          </div>
        </div>

        <div class="table-responsive overflow-auto mt-4">
          <table class="table table-striped">
            <thead>
            <tr>
              <th style="width: 200px;">Datum</th>
              <th>Grund / Thema</th>
              <th></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="report in interimReportList" :key="report.id"
                :class="['table-row']"
                @click="handleEditModalForm(+report.id)">
              <th>{{ report.createdAt }}</th>
              <th>{{ truncate(report?.topic, 180) }}</th>
              <td class="text-end" style="width: 100px!important;">
                <button class="btn btn-primary me-2"
                        @click.stop="downloadFile(`/portal/assisted-living-coaching/interim-report-pdf/${report.id}`)">
                  PDF
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <Modal
          v-model="showInterimReportModalForm"
          title="Zwischenbericht"
          :hide-cancel="true"
          :button-label="$t('general.button.save')"
          sizeModal="modal-xl"
          :hide-footer="true"
          classFooter="justify-content-center">
        <Form :coachingId="coachingId"
              :interimReportId="interimReportId"
              :actualConditions="actualConditions"
              :actualStates="actualStates"
              v-model:submitForm="submitForm"/>
      </Modal>

      <ModalDelete
          v-model="showDeleteModal"
          :header="t('general.delete_item_header')"
          :onDelete="deleteReport">
      </ModalDelete>
    </div>
  </PageTemplate>
</template>

<script setup>
import portalService from '@/api/Portal/AssistedLivingCoaching';
import coachingService from '@/api/Unemployed/AssistedLivingCoaching';
import Loader from '@/components/Loader.vue';
import Modal from '@/components/modal/Modal.vue';
import ModalDelete from '@/components/modal/ModalDelete.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import {downloadFile} from '@/shared/functions';
import {piniaUserStore} from '@/store/piniaUserStore';
import CoachingOverviewData from '@/views/App/Portal/AssistedLivingCoaching/CoachingOverviewData.vue';
import Form from '@/views/App/Portal/AssistedLivingCoaching/InterimReport/Form.vue';
import Menu from '@/views/App/Portal/AssistedLivingCoaching/Menu.vue';
import {computed, onMounted, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRoute} from 'vue-router';

const piniaUserState = piniaUserStore();
const piniaCurrentUser = computed(() => piniaUserState?.currentUser?.user);
const {t} = useI18n();
const route = useRoute();
const coachingData = ref('');
const interimReportId = ref('');
const interimReportList = ref([]);
const actualConditions = ref([]);
const actualStates = ref([]);
const coachingId = +route.params.id;
const isLoaded = ref(false);
const showInterimReportModalForm = ref(false);
const showDeleteModal = ref(false);
const submitForm = ref(false);
const calculatedUnitsData = ref();

watch(submitForm, () => {
  interimReportList.value = null;
  showInterimReportModalForm.value = false;
  showDeleteModal.value = false;
  getCoachingData();
  getInterimReportList();
});

async function getInterimReportList() {
  try {
    const {data} = await portalService.getInterimReportList(coachingId);
    interimReportList.value = data.data;
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getCoachingData() {
  try {
    CloseToast();
    const {data} = await coachingService.getAssistedLivingCoaching(+coachingId);
    coachingData.value = data.data;

    const {data: calculatedUnits} = await coachingService.getCalculatedUnits(+coachingId);
    calculatedUnitsData.value = calculatedUnits.data;
  } catch (error) {
    await ErrorToast(error);
  }
}

function handleModalForm() {
  CloseToast();
  interimReportId.value = '';
  showInterimReportModalForm.value = true;
  showDeleteModal.value = false;
}

function handleEditModalForm(id) {
  CloseToast();
  interimReportId.value = id;
  showInterimReportModalForm.value = true;
  showDeleteModal.value = false;
}

function handleDeleteModal(id) {
  CloseToast();
  interimReportId.value = id;
  showDeleteModal.value = true;
  showInterimReportModalForm.value = false;
}

async function deleteReport() {
  try {
    await portalService.deleteInterimReport(interimReportId.value);

    interimReportId.value = null;
    showDeleteModal.value = false;

    await getCoachingData();
    await getInterimReportList();
    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}

const truncate = (text, length) => {
  if (!text || typeof text !== 'string') {
    return ''; // Return an empty string if text is null, undefined, or not a string
  }

  const strippedText = text.replace(/<\/?[^>]+(>|$)/g, ''); // Remove HTML tags
  return strippedText.length > length ? strippedText.substring(0, length) + '...' : strippedText;
};

onMounted(async () => {
  await Promise.all([
    getCoachingData(),
    getInterimReportList(),
  ]);
  isLoaded.value = true;
});
</script>

<style scoped>

</style>